import {DatePicker, Input, Radio} from 'antd'
import {getId, getType} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik} from 'formik'
import {getDateString, parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext, useMemo} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {compose, mapProps} from 'recompose'
import {appClassNames} from 'views/AppLocals/custom/appClassNames'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {MedalSingleSelect} from 'views/Workspace/Medal/MedalSelect'
import {course_status_options, CourseActionTypes} from '..'
import CourseContext from '../CourseContext'
import Translate from "../../../../modules/local/Translate";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsShieldFillExclamation} from "react-icons/bs";
import dayjs from "dayjs";

const formSchema = {
    title: {
        title: 'title of course',
        children: [
            {
                name: 'title',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Input
                                {...{
                                    name,
                                    value,
                                    onChange,
                                    placeholder: t('title of course'),
                                }}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    description: {
        title: 'short description',
        children: [
            {
                name: 'description',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Input.TextArea
                                {...{
                                    name,
                                    value,
                                    onChange,
                                }}
                                rows={5}
                                placeholder={t('short description')}
                            />
                        )}
                    </Translate>

                ),
            },
        ],
    },
    last_enrollment_time: {
        title: 'last enrollment time',
        children: [
            {
                name: 'last_enrollment_time',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        showTime: true,
                        className: 'w-full',
                        format: 'HH:mm - DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 07:00 - 20/10/2010'),
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
        ],
    },
    status: {
        title: 'status',
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="custom-radio-button"
                        />
                    )
                },
            },
        ],
    },
    started_finished_date: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                name: 'time_open',
                label: 'start date',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        // showTime: true,
                        className: 'w-full',
                        format: 'DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 20/10/2010'),
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
            {
                name: 'time_close',
                label: 'end date',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        // showTime: true,
                        className: 'w-full',
                        format: 'DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 20/10/2010'),
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
        ],
    },
    medal_id: {
        title: 'certification',
        children: [
            {
                name: 'medal_id',
                component: ({name, form, value, onChange}) => {
                    const {medal, owner} = form?.values ?? {}
                    return (
                        <MedalSingleSelect
                            {...{
                                name,
                                value,
                                onChange,
                            }}
                            type="medal"
                            owner_id={getId(owner)}
                            defaultValue={getId(medal)}
                            owner_type={getType(owner)}
                            className="custom-select-medal"
                        />
                    )
                },
            },
        ],
    },
    privacy: {
        title: 'privacy',
        children: {
            name: 'privacy',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 2,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 2 : 1))
                    },
                    title: translate('privacy'),
                    description: translate("This parameter allows whether non-member users are restricted from access or not."),
                }))
            )(CustomToggleField),
        },
    },
    channel_post_permission: {
        title: 'posting permission',
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate("Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post"),

                }))
            )(CustomToggleField),
        },
    },
}

const CourseSettingOverview = () => {
    const t = useTranslate()

    const {item, updateAsync, handleAction = NullAsDefault} = useContext(CourseContext)

    const {isLoading} = updateAsync ?? {}

    const initialValues = useMemo(() => {
        const {medal, time_open, time_close, last_enrollment_time, ...rest} =
        item ?? {}
        const status_options = [
            course_status_options.normal,
            course_status_options.closed,
        ].map(deepTranslate(t))
        return {
            ...rest,
            medal,
            status_options,
            medal_id: getId(medal),
            time_open: parseDayjs(time_open),
            time_close: parseDayjs(time_close),
            last_enrollment_time: parseDayjs(last_enrollment_time),
        }
    }, [item])

    const validate = (values) => {
        let errors = {}

        const {title} = values ?? {}

        if (!title) {
            errors.title = t('required field')
        }

        return errors
    }

    const handleSubmit = (values) => {
        handleAction(CourseActionTypes.COURSE_UPDATE, values)
    }

    return (
        <div className="space-y-3 flex flex-col">
            <Formik
                validate={validate}
                validateOnMount={true}
                validateOnChange={true}
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={(values) => {
                    const changes = shallowDiff(values, initialValues)
                    const {
                        resetKey,
                        time_open,
                        time_close,
                        status_options,
                        last_enrollment_time,
                        ...rest
                    } = changes ?? {}
                    handleSubmit(
                        _.omitBy(
                            {
                                ...rest,
                                time_open: getDateString(time_open),
                                time_close: getDateString(time_close),
                                last_enrollment_time: getDateString(last_enrollment_time),
                            },
                            _.isUndefined
                        )
                    )
                }}>
                <React.Fragment>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.description,
                            formSchema.medal_id,
                            formSchema.started_finished_date,
                            formSchema.last_enrollment_time,
                        ]}
                    />

                    {/*<AdvancedFormFields formSchema={[formSchema.status]}/>*/}

                    <div className="flex flex-col gap-3">
                        <HeaderWithIconLine
                            className="pt-4 mt-3"
                            renderTitle={() => (
                                <div className="flex items-center gap-2">
                                    <BsShieldFillExclamation size={16} className="text-color-500"/>
                                    <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                        {t('privacy')}
                                    </span>
                                </div>
                            )}
                        />
                        <div className="ml-2 flex flex-col gap-3">
                            <FieldsFactory
                                formSchema={[
                                    formSchema.status,
                                    formSchema.privacy,
                                    formSchema.channel_post_permission,
                                ]}
                            />
                        </div>
                    </div>
                    <FormActionGroups
                        isSubmitting={isLoading}
                        submitProps={{
                            label: 'save',
                        }}
                    />
                </React.Fragment>
            </Formik>
        </div>
    )
}

export default CourseSettingOverview
